import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, GraduationCap, BookOpen, PiggyBank, Target, CheckCircle, Calendar, TrendingUp, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function EduFuturePage() {
  const navigate = useNavigate();

  const savingsPlans = [
    {
      title: 'Primary Education',
      duration: '5-10 years',
      minContribution: '₦5,000/month',
      targetAmount: '₦500,000 - ₦2,000,000',
      description: 'Start early and build a solid foundation for your child\'s education',
      icon: <BookOpen className="w-6 h-6" />
    },
    {
      title: 'Secondary Education',
      duration: '3-6 years',
      minContribution: '₦10,000/month',
      targetAmount: '₦1,000,000 - ₦5,000,000',
      description: 'Save for quality secondary school education and activities',
      icon: <GraduationCap className="w-6 h-6" />
    },
    {
      title: 'University Fund',
      duration: '4-10 years',
      minContribution: '₦20,000/month',
      targetAmount: '₦3,000,000 - ₦15,000,000',
      description: 'Prepare for higher education locally or abroad',
      icon: <Target className="w-6 h-6" />
    }
  ];

  const features = [
    { icon: <PiggyBank className="w-6 h-6" />, title: 'Automated Savings', desc: 'Set it and forget it with auto-debit' },
    { icon: <TrendingUp className="w-6 h-6" />, title: '12% Annual Returns', desc: 'Your savings grow while you save' },
    { icon: <Calendar className="w-6 h-6" />, title: 'Flexible Duration', desc: 'Plans from 1 to 18 years' },
    { icon: <Users className="w-6 h-6" />, title: 'Family Groups', desc: 'Save together with relatives' }
  ];

  const testimonials = [
    {
      name: 'Mrs. Adaeze Okonkwo',
      role: 'Mother of 3',
      quote: 'EduFuture helped me save ₦3.5M for my daughter\'s university education. The automated savings made it so easy!',
      avatar: 'A'
    },
    {
      name: 'Mr. Emeka Nwosu',
      role: 'Father of 2',
      quote: 'Started saving when my son was 2 years old. Now he\'s starting primary school with his fees fully covered.',
      avatar: 'E'
    },
    {
      name: 'Mrs. Fatima Hassan',
      role: 'Mother of 4',
      quote: 'The group savings feature let our extended family contribute to our children\'s education together.',
      avatar: 'F'
    }
  ];

  const steps = [
    { step: '01', title: 'Create Plan', desc: 'Choose your education savings goal' },
    { step: '02', title: 'Set Amount', desc: 'Decide monthly contribution' },
    { step: '03', title: 'Auto-Save', desc: 'Enable automatic deductions' },
    { step: '04', title: 'Watch It Grow', desc: 'Earn up to 12% annually' }
  ];

  const faqs = [
    {
      question: 'When can I withdraw my savings?',
      answer: 'You can withdraw at your plan\'s maturity date, or make early withdrawals with a small fee. Emergency education-related withdrawals may be fee-free.'
    },
    {
      question: 'What if I can\'t make a monthly contribution?',
      answer: 'You can pause contributions for up to 3 months without penalty. Your existing savings will continue to earn interest.'
    },
    {
      question: 'Can multiple family members contribute to one plan?',
      answer: 'Yes! Our Family Group feature allows grandparents, aunts, uncles, and other family members to contribute to a child\'s education fund.'
    },
    {
      question: 'Is my money safe?',
      answer: 'Absolutely. All deposits are insured by NDIC up to ₦500,000, and we\'re regulated by the Central Bank of Nigeria.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 md:px-8 bg-gradient-to-br from-[#221a83]/5 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 bg-indigo-50 text-brand-blue px-4 py-2 rounded-full text-sm font-medium">
                <GraduationCap className="w-4 h-4" />
                <span>EduFuture</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold font-heading text-slate-900 leading-tight">
                Secure Your Child's Educational Future
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed">
                Start saving today for primary school, secondary education, or university. 
                Earn up to 12% annual returns while building your child's education fund.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => navigate('/auth')}
                  className="bg-brand-blue text-white hover:bg-[#1a1466] rounded-full px-8 py-6 text-lg font-semibold"
                >
                  Create Education Plan
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  variant="outline"
                  className="rounded-full px-8 py-6 text-lg"
                >
                  Calculate Savings
                </Button>
              </div>
              <div className="flex items-center gap-8 pt-4">
                <div>
                  <p className="text-3xl font-bold text-brand-blue">₦5B+</p>
                  <p className="text-sm text-slate-600">Total Saved</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-brand-blue">25,000+</p>
                  <p className="text-sm text-slate-600">Active Plans</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-brand-blue">12%</p>
                  <p className="text-sm text-slate-600">Annual Returns</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1503676260728-1c00da094a0b?q=80&w=2022&auto=format&fit=crop"
                alt="Education"
                className="rounded-3xl shadow-2xl w-full h-[450px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-xl p-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-green-100 text-green-600 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Your Fund Growing</p>
                    <p className="text-xl font-bold text-slate-900">+₦45,000</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Savings Plans Section */}
      <section className="py-20 px-4 md:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-slate-900 mb-4">
              Education Savings Plans
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Choose the right plan for your child's educational journey
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {savingsPlans.map((plan, idx) => (
              <div 
                key={idx}
                className="bg-white rounded-3xl p-8 shadow-sm border border-slate-200 hover:border-brand-blue/50 transition-all hover:-translate-y-1"
              >
                <div className="w-14 h-14 rounded-2xl bg-indigo-50 text-brand-blue flex items-center justify-center mb-6">
                  {plan.icon}
                </div>
                <h3 className="text-xl font-bold font-heading text-slate-900 mb-2">{plan.title}</h3>
                <p className="text-slate-600 mb-4">{plan.description}</p>
                <div className="space-y-2 pt-4 border-t border-slate-100">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Duration</span>
                    <span className="font-semibold text-slate-900">{plan.duration}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Min. Contribution</span>
                    <span className="font-semibold text-brand-blue">{plan.minContribution}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Target Amount</span>
                    <span className="font-semibold text-slate-900">{plan.targetAmount}</span>
                  </div>
                </div>
                <Button 
                  onClick={() => navigate('/auth')}
                  className="w-full mt-6 bg-brand-blue text-white hover:bg-[#1a1466] rounded-full"
                >
                  Start This Plan
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold font-heading text-slate-900 mb-6">
                Why Parents Love EduFuture
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                More than just savings - it's a complete education planning solution that helps 
                Nigerian families prepare for the rising costs of quality education.
              </p>
              <div className="grid grid-cols-2 gap-6">
                {features.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-indigo-50 text-brand-blue flex items-center justify-center flex-shrink-0">
                      {feature.icon}
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900">{feature.title}</h4>
                      <p className="text-sm text-slate-600">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?q=80&w=2070&auto=format&fit=crop"
                alt="Students"
                className="rounded-3xl shadow-xl w-full h-[450px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-4 md:px-8 bg-gradient-to-br from-[#221a83] to-[#1a1466] text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-heading mb-4">
              Start In 4 Simple Steps
            </h2>
            <p className="text-lg text-indigo-100 max-w-2xl mx-auto">
              Begin your child's education savings journey today
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {steps.map((step, idx) => (
              <div key={idx} className="text-center">
                <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm text-white flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  {step.step}
                </div>
                <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                <p className="text-indigo-100">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-slate-900 mb-4">
              What Parents Are Saying
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Join thousands of Nigerian parents securing their children's future
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, idx) => (
              <div 
                key={idx}
                className="bg-white rounded-3xl p-8 shadow-sm border border-slate-200"
              >
                <p className="text-slate-600 mb-6 italic">"{testimonial.quote}"</p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-brand-blue text-white flex items-center justify-center font-bold text-lg">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <p className="font-bold text-slate-900">{testimonial.name}</p>
                    <p className="text-sm text-slate-600">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 md:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <div 
                key={idx}
                className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200"
              >
                <h3 className="font-bold text-slate-900 mb-2">{faq.question}</h3>
                <p className="text-slate-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 md:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-heading text-slate-900 mb-4">
            Give Your Child The Gift Of Education
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Start with as little as ₦5,000 per month and watch your education fund grow
          </p>
          <Button 
            onClick={() => navigate('/auth')}
            className="bg-brand-blue text-white hover:bg-[#1a1466] rounded-full px-12 py-7 text-xl font-semibold"
          >
            Start Saving Today
            <ArrowRight className="ml-2 w-6 h-6" />
          </Button>
          <p className="mt-4 text-sm text-slate-500">
            No hidden fees • Cancel anytime • NDIC insured
          </p>
        </div>
      </section>

      <Footer />

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-8 px-4 md:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <p>&copy; 2024 Britedge Financial Services. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
